import React, { useEffect, useState, useRef } from "react";
import ChatMessage from "./ChatMessage";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  Container,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import { AddchatTicket, ListChatTicket } from "../../services/ticket/ticket";
import { useLocation, useParams } from "react-router-dom";
import { convertToJalali } from "../../utils/convertToJalali";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [fileOnlyError, setFileOnlyError] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const { idstrticket } = useParams();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const { t } = useTranslation();

  const ticketTitle = location.state?.title;
  const ticketStatus = location.state?.status;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (idstrticket) fetchChat();
  }, [idstrticket]);

  const fetchChat = async () => {
    try {
      const { data } = await ListChatTicket(idstrticket); // Use idstrticket as per API spec
      if (data?.length > 0) {
        const formattedMessages = data.map((item) => ({
          type: item.typeuser === 2 ? "client" : "support",
          isClient: item.typeuser === 2,
          text: item.comment,
          idstrchat: item.idstrchat,
          file: item.pathfile || null,
          fileName: item.pathfile ? item.pathfile.split("/").pop() : null,
          date: item.datesabt,
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSend = async () => {
    const trimmedMessage = newMessage.trim();

    // Show error only if no text and no file
    if (!idstrticket || (!trimmedMessage && !selectedFile)) {
      setFileOnlyError(true);
      return;
    }

    // Only show error if text is missing (but allow file)
    if (!trimmedMessage) {
      setFileOnlyError(true);
    } else {
      setFileOnlyError(false);
    }

    const formData = new FormData();
    formData.append("idticket", 0);
    formData.append("idstrticket", idstrticket);
    formData.append("comment", trimmedMessage);
    formData.append("pathfile", "");
    formData.append("file", selectedFile || "");

    try {
      await AddchatTicket(formData);
      setNewMessage("");
      toast.success((t("Toast.success")));
      setSelectedFile(null);
      setFileOnlyError(false);
      fetchChat();
    } catch (error) {
      toast.error(error.response?.data || (t("Toast.error")));
    }
  };
  return (
    <Box className="flex flex-col " sx={{ overflow: "hidden" }} dir="rtl">
      {/* Sticky Title */}
      <Container
        sx={{
          position: "fixed",
          top: 60,
          left: 0,
          right: 0,
          zIndex: 5,
          backgroundColor: "rgba(255,255,255,0.7)",
          backdropFilter: "blur(8px)",
          paddingTop: 3,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <Typography sx={{ textAlign: "center" }} variant="h6">
          {ticketTitle}
        </Typography>
      </Container>

      {/* Messages */}
      <Box className="overflow-y-auto px-4 py-[80px]">
        {messages.map((msg, idx) => (
          <ChatMessage
            key={idx}
            type={msg.type}
            isClient={msg.isClient}
            download={msg.idstrchat}
            text={msg.text}
            date={convertToJalali(msg.date)}
            file={msg.file}
            fileName={msg.fileName}
          />
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Container
        sx={{
          position: "fixed",
          bottom: 75,
          left: 0,
          right: 0,
          zIndex: 10,
          backgroundColor: "rgba(255,255,255,0.7)",
          backdropFilter: "blur(8px)",
          padding: 2,
        }}
      >
        {ticketStatus === "بسته شده" ? (
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid #f5c6cb",
            }}
          >
            <Typography sx={{ fontWeight: "500" }}>
              {t("Support.Close")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              sx={{
                backgroundColor: "#512c63",
                color: "white",
                "&:hover": {
                  backgroundColor: "#512c63",
                  color: "white",
                },
              }}
              onClick={handleSend}
            >
              <SendIcon />
            </IconButton>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              placeholder={(t("Support.placeholder"))}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                if (fileOnlyError) setFileOnlyError(false); // Reset error
              }}
              error={fileOnlyError && !newMessage.trim()}
              helperText={
                fileOnlyError && !newMessage.trim()
                  ? (t("Support.mustwrite"))
                  : ""
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton component="label">
                      <AttachFileIcon />
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const maxSizeMB = 30;
                            const allowedTypes = [
                              "image/jpeg",
                              "image/png",
                              "application/pdf",
                              "application/msword",
                            ];

                            if (!allowedTypes.includes(file.type)) {
                              alert(
                                (t("Support.Format"))
                              );
                              return;
                            }

                            if (file.size > maxSizeMB * 1024 * 1024) {
                              alert((t("Support.30mb")));
                              return;
                            }

                            setSelectedFile(file);
                          }
                        }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {selectedFile && (
          <Typography mt={1} fontSize={14} color="text.secondary">
                {(t("Support.File"))}
                 {selectedFile.name}
          </Typography>
        )}
      </Container>
    </Box>
  );
}
